import { TagType } from "@/models/tag/tag";
import { useEffect, useState } from "react";
import usePromptLibrary from "../../hooks/usePromptLibrary";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  ArrowLeft,
  CircleHelpIcon,
  FileText,
  Tag,
  Building2,
} from "lucide-react";
import { Input } from "@capacit-com/lara-law-shared-ui";
import MultiSelectField from "../filter/MultiSelectField";
import { Textarea } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import Prompt, { PromptStatus } from "@/models/prompt/prompt";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";

type Props = {
  selectedPrompt: Prompt | undefined;
  handleBackClicked: () => void;
  onFormChange: (hasChanged: boolean) => void;
};

export type PromptSchemaType = {
  title: string;
  description: string;
  promptString: string;
  tags: {
    name: string;
    tagType: TagType;
  }[];
  status: PromptStatus;
};

const PromptLibraryFormRedesigned = ({
  selectedPrompt,
  handleBackClicked,
  onFormChange,
}: Props) => {
  const { tags, handleCreatePrompt, handleUpdatePrompt, crudPending } =
    usePromptLibrary();

  const [initialPrompt] = useState<PromptSchemaType>({
    title: selectedPrompt?.title || "",
    description: selectedPrompt?.description || "",
    promptString: selectedPrompt?.promptString || "",
    tags: selectedPrompt?.tags || [],
    status: selectedPrompt?.status || PromptStatus.Personal,
  });

  // Form validation schema
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .max(50, "Titel må højst være 50 tegn")
      .required("Titel er påkrævet"),
    description: Yup.string()
      .max(450, "Beskrivelse må højst være 450 tegn")
      .required("Beskrivelse er påkrævet"),
    promptString: Yup.string().required("Prompt er påkrævet"),
    tags: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Tag-navn er påkrævet"),
        tagType: Yup.mixed<TagType>()
          .oneOf(Object.values(TagType) as TagType[])
          .required("Tag-type er påkrævet"),
      })
    ),
    status: Yup.mixed<PromptStatus>()
      .oneOf(Object.values(PromptStatus) as PromptStatus[])
      .required("Prompt-status er påkrævet"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: initialPrompt,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      values.title = values.title.trim();
      values.description = values.description.trim();
      values.promptString = values.promptString.trim();

      if (selectedPrompt) {
        // Edit mode
        if (await handleUpdatePrompt(selectedPrompt.id, values)) {
          handleBackClicked();
        }
      } else {
        if (await handleCreatePrompt(values)) {
          handleBackClicked();
        }
      }
    },
  });

  useEffect(() => {
    const hasChanged =
      JSON.stringify(formik.values) !== JSON.stringify(initialPrompt);
    onFormChange(hasChanged);
  }, [formik.values, initialPrompt, onFormChange]);

  return (
    <div className="flex-1 h-full flex flex-col min-h-0">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.preventDefault();
              handleBackClicked();
            }}
            className="h-9 w-9"
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <p className="text-base text-foreground">
              {selectedPrompt
                ? "Opdater din prompt herunder"
                : "Udfyld formularen for at oprette en ny prompt"}
            </p>
          </div>
        </div>
      </div>

      <form
        onSubmit={formik.handleSubmit}
        className="flex-1 flex flex-col gap-2 min-h-0 overflow-hidden"
      >
        <div className="flex-1 overflow-y-auto px-1 py-1 space-y-6">
          {/* Basic Information Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <FileText className="size-4" />
                Grundlæggende information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Title Field */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label
                    htmlFor="title"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Titel <span className="text-destructive">*</span>
                  </label>
                  <span
                    className={cn(
                      "text-xs font-medium transition-colors",
                      formik.values.title.length > 45
                        ? "text-warning"
                        : formik.values.title.length === 50
                          ? "text-destructive"
                          : "text-muted-foreground"
                    )}
                  >
                    {formik.values.title.length}/50
                  </span>
                </div>
                <Input
                  id="title"
                  name="title"
                  placeholder="Indtast en beskrivende titel..."
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  maxLength={50}
                  className={cn(
                    formik.errors.title &&
                      formik.touched.title &&
                      "border-destructive"
                  )}
                />
                {formik.errors.title && formik.touched.title && (
                  <div className="text-destructive text-xs flex items-center gap-1">
                    {formik.errors.title}
                  </div>
                )}
              </div>

              {/* Status Checkbox */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="status"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Anmod om offentliggørelse
                  </label>
                  <Tooltip delayDuration={10}>
                    <TooltipTrigger asChild>
                      <CircleHelpIcon className="size-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>
                        Anmod en promptmoderator til at offentliggøre prompten
                        så den er tilgængelig for alle i organisationen.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="status"
                    name="status"
                    checked={formik.values.status === PromptStatus.Pending}
                    onCheckedChange={(checked) => {
                      formik.setFieldValue(
                        "status",
                        checked ? PromptStatus.Pending : PromptStatus.Personal
                      );
                    }}
                    onBlur={formik.handleBlur}
                  />
                  <label
                    htmlFor="status"
                    className="text-sm text-muted-foreground cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Anmod om at denne prompt bliver offentliggjort
                  </label>
                </div>
                {formik.errors.status && formik.touched.status && (
                  <div className="text-destructive text-xs">
                    {formik.errors.status}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Categories & Tags Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Tag className="size-4" />
                Kategorisering
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Department Field */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="size-4 text-muted-foreground" />
                    <label className="text-sm font-medium">Afdelinger</label>
                  </div>
                  <MultiSelectField
                    placeholder="Vælg afdeling"
                    label=""
                    options={tags.filter(
                      (tag) => tag.tagType === TagType.Department
                    )}
                    value={formik.values.tags.filter(
                      (tag) => tag.tagType === TagType.Department
                    )}
                    onChange={(option) => {
                      const currentArray = [...formik.values.tags];

                      if (
                        currentArray.some((tag) => tag.name === option.name)
                      ) {
                        currentArray.splice(
                          currentArray.findIndex(
                            (tag) => tag.name === option.name
                          ),
                          1
                        );
                      } else {
                        if (
                          currentArray.some(
                            (tag) => tag.tagType === TagType.Department
                          )
                        ) {
                          currentArray.splice(
                            currentArray.findIndex(
                              (tag) => tag.tagType === TagType.Department
                            ),
                            1
                          );
                        }
                        currentArray.push(option);
                      }
                      formik.setFieldValue("tags", currentArray);
                    }}
                    onClear={() => {
                      formik.setFieldValue(
                        "tags",
                        formik.values.tags.filter(
                          (tag) => tag.tagType !== TagType.Department
                        )
                      );
                    }}
                    optionKey="name"
                  />
                </div>

                {/* Tags Field */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="size-4 text-muted-foreground" />
                    <label className="text-sm font-medium">Tags</label>
                    <span className="text-xs text-muted-foreground">
                      (max 4)
                    </span>
                  </div>
                  <MultiSelectField
                    label=""
                    options={tags.filter((tag) => tag.tagType === TagType.Tag)}
                    value={formik.values.tags.filter(
                      (tag) => tag.tagType === TagType.Tag
                    )}
                    placeholder="Vælg tags"
                    onChange={(option) => {
                      const currentArray = [...formik.values.tags];

                      if (
                        currentArray.some((tag) => tag.name === option.name)
                      ) {
                        currentArray.splice(
                          currentArray.findIndex(
                            (tag) => tag.name === option.name
                          ),
                          1
                        );
                      } else {
                        if (
                          currentArray.filter(
                            (tag) => tag.tagType === TagType.Tag
                          ).length === 4
                        ) {
                          return;
                        }

                        currentArray.push(option);
                      }
                      formik.setFieldValue("tags", currentArray);
                    }}
                    optionKey="name"
                    onClear={() => {
                      formik.setFieldValue(
                        "tags",
                        formik.values.tags.filter(
                          (tag) => tag.tagType !== TagType.Tag
                        )
                      );
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <FileText className="size-4" />
                Beskrivelse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between items-center">
                <label
                  htmlFor="description"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Beskriv formålet med prompten{" "}
                  <span className="text-destructive">*</span>
                </label>
                <span
                  className={cn(
                    "text-xs font-medium transition-colors",
                    formik.values.description.length > 400
                      ? "text-warning"
                      : formik.values.description.length === 450
                        ? "text-destructive"
                        : "text-muted-foreground"
                  )}
                >
                  {formik.values.description.length}/450
                </span>
              </div>
              <Textarea
                id="description"
                name="description"
                placeholder="Beskriv hvad denne prompt kan bruges til og hvilken situation den er bedst egnet til..."
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                maxLength={450}
                rows={4}
                className={cn(
                  "resize-none",
                  formik.errors.description &&
                    formik.touched.description &&
                    "border-destructive"
                )}
              />
              {formik.errors.description && formik.touched.description && (
                <div className="text-destructive text-xs">
                  {formik.errors.description}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Prompt Content Card */}
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <FileText className="size-4" />
                Prompt indhold
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0 space-y-2">
              <label
                htmlFor="promptString"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Prompt <span className="text-destructive">*</span>
              </label>
              <Textarea
                id="promptString"
                name="promptString"
                placeholder="Indtast din prompt her... Skriv klart og præcist for bedste resultater."
                value={formik.values.promptString}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={cn(
                  "resize-none w-full flex-1 min-h-[200px]",
                  formik.errors.promptString &&
                    formik.touched.promptString &&
                    "border-destructive"
                )}
              />
              {formik.errors.promptString && formik.touched.promptString && (
                <div className="text-destructive text-xs">
                  {formik.errors.promptString}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t shrink-0 bg-background">
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              handleBackClicked();
            }}
            className="flex-1"
          >
            Annuller
          </Button>
          <Button
            type="submit"
            className="flex-1"
            disabled={!formik.isValid || crudPending}
          >
            {crudPending ? (
              <>
                <Spinner className="mr-2 size-4" />
                Gemmer...
              </>
            ) : (
              <>{selectedPrompt ? "Opdater Prompt" : "Opret Prompt"}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PromptLibraryFormRedesigned;
